import React, { useEffect } from 'react';
import { Flex, Text } from '@indeed/ifl-components';
import { Information } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { useGetScreenerQuestionLabelDataQuery } from './__generated__/ScreenerQuestionLabel.generated';

interface ScreenerQuestionLabelProps {
    applicationDraftId: string;
    onLabelChange?: (applicationDraftId: string, label: string | null) => void;
}

export const ScreenerQuestionLabel: React.FC<ScreenerQuestionLabelProps> = ({
    applicationDraftId,
    onLabelChange,
}) => {
    const gt = GettextFactory();
    const includedLabel = gt.gettext('Employer questions included');
    const fallbackLabel = gt.gettext('Employer questions may be included');
    const { data, loading, error } = useGetScreenerQuestionLabelDataQuery({
        variables: { applicationDraftId },
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
    });
    const hasScreenerQuestions =
        data?.findApplicationDrafts?.operationDetails?.some(
            (draft) =>
                draft.applicationDraftId === applicationDraftId &&
                (draft.questionsAndAnswers?.screener?.length ?? 0) > 0,
        ) ?? false;
    const label = hasScreenerQuestions ? includedLabel : error ? fallbackLabel : null;

    useEffect(() => {
        if (!loading) {
            onLabelChange?.(applicationDraftId, label);
        }
    }, [applicationDraftId, label, loading, onLabelChange]);

    if (!label) {
        return null;
    }

    return (
        <Flex
            aria-hidden={onLabelChange ? true : undefined}
            sx={{
                alignItems: 'flex-start',
                gap: 1,
                paddingBlockStart: 2,
            }}
        >
            <Information
                size="xs"
                variant="stroke"
                color="foreground.weak"
                aria-hidden
                sx={{ flexShrink: 0 }}
            />
            <Text level={1} variant="secondary" sx={{ fontWeight: 'normal' }}>
                {label}
            </Text>
        </Flex>
    );
};
